// Carer-portal API client (mobile-responsive web at /carer/*).
// Uses a separate `carer_token` in localStorage so it can coexist with the
// coordinator session on the same browser. Hits the same /hcm/api/v1 surface
// as the React Native mobile app — no backend changes are required.
//
// Carer-parity S2 / D1c: token storage I/O writes localStorage directly
// rather than delegating to setAccessToken (which routes to whichever
// BearerProvider is active). Reason: carerFetch's silent-refresh path
// can fire AFTER the carer subtree unmounts (in-flight fetch + user
// navigates away); delegating would write the new carer bearer to whichever
// provider replaced it (e.g., the coordinator's sessionStorage). Direct
// localStorage:carer_token I/O is unconditional and cross-mount safe.
// The carer <BearerProvider> reads localStorage on each getBearer() call
// so writes from this module remain visible through the provider naturally.
// Module-scoped `carerToken` cache dropped (localStorage reads are fast and
// the cache became a stale-state hazard).

// In dev, leave VITE_API_BASE unset so the path stays relative ("/hcm/api/v1")
// and the Vite proxy forwards to localhost:3000. In production set VITE_API_BASE
// to the API origin (e.g. https://api.hcmcrm.com).
const HCM_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";

const TOKEN_KEY   = "carer_token";
const REFRESH_KEY = "carer_refresh_token";

export function setCarerToken(token: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function getCarerToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearCarerToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Silent refresh — called by carerFetch on 401 before redirecting to /carer/login.
// Uses /auth/mobile-refresh because it accepts the refresh token in the body and
// returns a new pair (the cookie-based /auth/refresh path returns access_token only).
async function tryRefreshToken(): Promise<boolean> {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return false;
  try {
    const res = await fetch(`${HCM_BASE}/auth/mobile-refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { access_token?: string; refresh_token?: string };
    if (!data.access_token) return false;
    setCarerToken(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export class CarerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "CarerApiError";
  }
}

// On 401, try a silent refresh once before bouncing the carer to login.
// Visit reports take 5–15 min to fill in; without this, the access token
// expires mid-form and submission redirects to login, losing the entry.
async function recoverFromUnauthorized(
  doFetch: (bearer: string | null) => Promise<Response>,
): Promise<Response> {
  const refreshed = await tryRefreshToken();
  if (refreshed) return doFetch(getCarerToken());
  clearCarerToken();
  if (typeof window !== "undefined" && !window.location.pathname.endsWith("/login")) {
    window.location.href = "/carer/login";
  }
  throw new CarerApiError(401, null, "Session expired");
}

async function throwApiError(res: Response): Promise<never> {
  let body: unknown = null;
  try { body = await res.json(); } catch { /* empty body */ }
  const errMsg = (body as { error?: string } | null)?.error ?? `Request failed: ${res.status}`;
  throw new CarerApiError(res.status, body, errMsg);
}

export async function carerFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const doFetch = async (bearer: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
    return fetch(`${HCM_BASE}${path}`, { ...init, headers });
  };

  const token = getCarerToken();
  let res = await doFetch(token);

  if (res.status === 401 && token) res = await recoverFromUnauthorized(doFetch);
  if (!res.ok) await throwApiError(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface Visit {
  _id: string;
  patient_id: string;
  patient_name: string | null;
  service_type: string;
  status: string;
  scheduled_datetime: string;
  // SR records can be missing both fields when prepopulated minimally.
  address?: string | null;
  special_instructions?: string | null;
  notes?: string | null;
  assigned_carer_id?: string;
  report_submitted_at?: string | null;
  /** Carer-parity S3a — feeds the wizard's step-0 visit-type fallback. */
  visit_type?: string | null;
}

export interface Medication {
  _id: string;
  drug_name?: string;
  generic_name?: string;
  dosage_mg?: number;
  dosage_unit?: string;
  frequency?: string;
  status?: string;
}

export interface VisitReport {
  _id: string;
  sr_id: string;
  submitted_at?: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; tenant_id: string; must_change_password?: boolean };
}

// ── API surface used by the carer pages ──────────────────────────────────────

export const carerApi = {
  login: (email: string, password: string): Promise<LoginResponse> =>
    carerFetch<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  forgotPassword: (email: string): Promise<{ message: string }> =>
    carerFetch<{ message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  getMyVisits: (): Promise<{ data: Visit[]; total: number; page: number; limit: number }> =>
    carerFetch<{ data: Visit[]; total: number; page: number; limit: number }>(
      "/sr?assigned_to_me=true&status=new,assigned,scheduled,in_progress&limit=50",
    ),

  getVisit: (id: string): Promise<Visit> =>
    carerFetch<Visit>(`/sr/${id}`),

  getVisitReport: async (srId: string): Promise<VisitReport | null> => {
    try {
      return await carerFetch<VisitReport>(`/visit-reports/by-sr/${srId}`);
    } catch (err) {
      if (err instanceof CarerApiError && err.status === 404) return null;
      throw err;
    }
  },

  getMedications: async (patientId: string): Promise<Medication[]> => {
    try {
      const res = await carerFetch<{ data: Medication[]; total: number; page: number; limit: number }>(
        `/patients/${patientId}/medications`,
      );
      return res.data ?? [];
    } catch {
      return [];
    }
  },

  updateVisitStatus: (id: string, status: string): Promise<Visit> =>
    carerFetch<Visit>(`/sr/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // Carer-parity S3a: the carer report page now mounts <VisitReportWizard
  // submissionChannel="mobile">, which scatters per-section PATCHes via
  // visit-report-api.ts (single source of truth). The legacy
  // submitVisitReport scatter shim — duplicated CARER_SECTION_FIELDS +
  // scatterCarerSubmit — was retired.

  flagSuspiciousVitals: (srId: string, patientId: string, fields: string[]): Promise<void> =>
    carerFetch<void>("/notifications/flag-suspicious-vitals", {
      method: "POST",
      body: JSON.stringify({ sr_id: srId, patient_id: patientId, suspicious_fields: fields }),
    }).catch(() => { /* non-fatal — coordinator alert is best-effort */ }),

  // Refreshes the 30-min carer inactivity TTL in Redis (see middleware/auth.ts).
  // Called from long-form pages (visit report) on a 5-min interval so the carer
  // doesn't get bounced mid-form. Best-effort: failure is silent.
  keepalive: (): Promise<void> =>
    carerFetch<void>("/auth/heartbeat", { method: "GET" })
      .catch(() => { /* network blip — next tick will retry */ }),
};

// ── Display helpers shared by all carer pages ────────────────────────────────

export const SERVICE_LABELS: Record<string, string> = {
  elder_care_visit:    "Elder Care Visit",
  post_discharge_care: "Post Discharge Care",
  companion_visit:     "Companion Visit",
  medicine_delivery:   "Medicine Delivery",
  medical_nursing:     "Medical Nursing",
  tele_consult:        "Teleconsult",
  other:               "Other",
};

export const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  new:         { label: "New",         color: "text-amber-700",  bg: "bg-amber-50  border-amber-200"  },
  assigned:    { label: "Assigned",    color: "text-navy-800",   bg: "bg-navy-50   border-navy-200"   },
  scheduled:   { label: "Scheduled",   color: "text-blue-700",   bg: "bg-blue-50   border-blue-200"   },
  in_progress: { label: "In Progress", color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200" },
  completed:   { label: "Completed",   color: "text-green-700",  bg: "bg-green-50  border-green-200"  },
  cancelled:   { label: "Cancelled",   color: "text-red-700",    bg: "bg-red-50    border-red-200"    },
};
